"use client";
import React from "react";

import { Box, Icon, Target, ThemeColors } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";

export type BannerType = "info" | "success" | "warning" | "critical";

export interface BannerProps {
  onDismiss?: () => void;
  variant: BannerType;
  title?: string;
  text: string | React.ReactNode;
}

interface BannerStyles {
  backgroundColor: ThemeColors;
  borderColor: ThemeColors;
  icon: "info" | "check-circle" | "warning-circle" | "x-circle";
}

export const bannerColors: Record<BannerType, BannerStyles> = {
  info: {
    backgroundColor: "feedback-surface-info",
    borderColor: "feedback-onSurface-incidental-info",
    icon: "info"
  },
  success: {
    backgroundColor: "feedback-surface-success",
    borderColor: "feedback-onSurface-incidental-success",
    icon: "check-circle"
  },
  warning: {
    backgroundColor: "feedback-surface-warning",
    borderColor: "feedback-onSurface-incidental-warning",
    icon: "warning-circle"
  },
  critical: {
    backgroundColor: "feedback-surface-critical",
    borderColor: "feedback-onSurface-incidental-critical",
    icon: "x-circle"
  }
};

export const Banner: React.FC<BannerProps> = (props: BannerProps) => {
  const { variant, title, text, onDismiss } = props;

  const bannerStyles = bannerColors[variant];

  return (
    <Box
      sx={{
        backgroundColor: bannerStyles.backgroundColor,
        borderLeft: "4px solid",
        borderColor: bannerStyles.borderColor,
        paddingInlineStart: "spacing-5",
        width: "100%"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between"
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "spacing-5",
            paddingTop: "spacing-4",
            paddingBottom: "spacing-4"
          }}
        >
          <Icon
            sx={{ color: bannerStyles.borderColor }}
            icon={bannerStyles.icon}
            size="md"
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              color: "text-default"
            }}
          >
            <Text variant="body-1-bold">{title}</Text>
            <Text variant="body-1-regular">{text}</Text>
          </Box>
        </Box>
        {onDismiss && (
          <Target
            aria-label="Dismiss message"
            onClick={onDismiss}
            sx={{
              display: "flex",
              flexShrink: 0,
              height: "spacing-10",
              width: "spacing-10",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Icon icon="x" size="sm" />
          </Target>
        )}
      </Box>
    </Box>
  );
};
